// Divisions on the public site (#583, docs/divisions/API.md §5, ARCHITECTURE
// §6, UX §5.1): the pure rules behind useDivision(). Which divisions are live,
// where the fan's pick is stored, which routes show the switcher, and how the
// URL, the stored pick and the page default combine.
//
// The Classic invariant: a sport with fewer than 2 live divisions has no
// switcher, no `division` param and no new request. Every rule here returns
// "disabled" for it, and the controllers then run exactly today's code.

import { matchPath } from 'react-router-dom'
import type { TenantDivision } from '@/contexts/LeagueContext'
import { glossary } from '../labels/glossary'
import { sportSwitcherHiddenOn } from '../chrome/useSportSwitcher'

/** A pick: one division's id, or every division. */
export type DivisionValue = number | 'all'

/** The URL and storage token for "every division" (`?division=all`). */
export const ALL_DIVISIONS = 'all' as const

/**
 * The live divisions of a sport, in admin order: not archived (the payload
 * lists only those) and with a public season (API §4.1). Undefined (an older
 * payload or cached tenant) is [].
 */
export function liveDivisions(
  league: { divisions?: readonly TenantDivision[] | null } | null | undefined,
  sportId: string | null | undefined,
): TenantDivision[] {
  if (!sportId) return []
  return (league?.divisions ?? [])
    .filter((d) => d.sport_id === sportId && d.public_season_id != null)
    .sort((a, b) => a.display_order - b.display_order || a.id - b.id)
}

/** The switcher (and every division param) needs 2+ live divisions. */
export function divisionsEnabled(live: readonly TenantDivision[]): boolean {
  return live.length >= 2
}

/**
 * List routes where the switcher shows: Home, Schedule, Stats, Teams and
 * Players (UX §5.1). Detail routes (SPORT_SWITCHER_HIDDEN_ON) never show it
 * and ignore `?division=`.
 */
export const DIVISION_LIST_ROUTES = ['/', '/schedule', '/stats', '/teams', '/players'] as const

export function isDivisionListRoute(pathname: string): boolean {
  return DIVISION_LIST_ROUTES.some((path) => matchPath({ path, end: true }, pathname) != null)
}

/**
 * Stats offers no "All divisions" (leaders across Youth and Men's mean
 * nothing, UX §5.1). Every other list page does.
 */
export function divisionRouteAllowsAll(pathname: string): boolean {
  return matchPath({ path: '/stats', end: true }, pathname) == null
}

/** The fan's pick, per host AND sport, beside the stored sport (`mcl_active_sport_<host>`). */
export function divisionStorageKey(sportId: string): string {
  return `mcl_active_division_${window.location.hostname}_${sportId}`
}

/** The stored pick: a division id, 'all', or null (nothing stored, or storage unavailable). */
export function readStoredDivision(sportId: string): DivisionValue | null {
  let raw: string | null = null
  try { raw = localStorage.getItem(divisionStorageKey(sportId)) } catch { return null }
  if (raw === ALL_DIVISIONS) return ALL_DIVISIONS
  const id = raw != null && /^\d+$/.test(raw) ? Number(raw) : NaN
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

export function writeStoredDivision(sportId: string, value: DivisionValue): void {
  try {
    localStorage.setItem(divisionStorageKey(sportId), String(value))
  } catch {
    // Private mode: the pick still works for this page view.
  }
}

/**
 * `?division=<slug>` → a pick. 'all' is All divisions; a slug must name a live
 * division of the sport. Anything else is ignored (null).
 */
export function parseDivisionParam(raw: string | null | undefined, live: readonly TenantDivision[]): DivisionValue | null {
  if (!raw) return null
  const slug = raw.trim().toLowerCase()
  if (slug === ALL_DIVISIONS) return ALL_DIVISIONS
  return live.find((d) => d.slug === slug)?.id ?? null
}

export interface DivisionResolveInput {
  live: readonly TenantDivision[]
  /** The pick from `?division=` (already parsed), only on a list route. */
  url: DivisionValue | null
  stored: DivisionValue | null
  /** The page offers "All divisions" (every list page except Stats). */
  allowAll: boolean
}

/**
 * The active pick. Precedence: URL → stored → page default (DECISIONS 6: All
 * where allowed, else the first live division). A stored id that is no longer
 * live falls through to the default; a page without All turns All into the
 * first division (without changing what's stored).
 */
export function resolveDivision({ live, url, stored, allowAll }: DivisionResolveInput): DivisionValue | null {
  if (!divisionsEnabled(live)) return null
  const valid = (v: DivisionValue | null): v is DivisionValue => v === ALL_DIVISIONS || (v != null && live.some((d) => d.id === v))
  const pick = valid(url) ? url : valid(stored) ? stored : ALL_DIVISIONS
  return pick === ALL_DIVISIONS && !allowAll ? live[0].id : pick
}

/** One switcher option. `slug` is null for All divisions. */
export interface DivisionOption {
  value: DivisionValue
  label: string
  slug: string | null
}

/** All divisions first (where allowed), then the live divisions in admin order, by full name. */
export function divisionOptions(live: readonly TenantDivision[], allowAll: boolean): DivisionOption[] {
  const each = live.map((d) => ({ value: d.id as DivisionValue, label: d.name, slug: d.slug }))
  return allowAll ? [{ value: ALL_DIVISIONS, label: glossary().allDivisions, slug: null }, ...each] : each
}

/**
 * Whether `?division=` is honoured on this route: list routes only. Detail
 * routes ignore it, and so do routes that hide the sport switcher.
 */
export function divisionParamApplies(pathname: string): boolean {
  return isDivisionListRoute(pathname) && !sportSwitcherHiddenOn(pathname)
}
